import React, { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';

import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { BylineConnectionIconStack } from '../BylineConnectionIconStack.tsx';
import {
	type BylineConnectionIcon,
	MAX_BYLINE_CONNECTION_ICONS,
} from '../utils/getBylineConnectionIcons.ts';

const i18n = defineMessages({
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
	teamworkGraph: {
		id: 'byline-connections.teamwork-graph-label',
		defaultMessage: 'Teamwork Graph',
		description: 'Label for the Teamwork Graph byline item shown in the page byline area',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		whiteSpace: 'normal',
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
	},
});

const getPreloadedOutboundLinkCount = (): number => {
	const smartLinkStats = window['__SSR_SMARTLINKS_STATS__'];
	const appearances = Object.values(smartLinkStats?.links ?? {}).flatMap((appearancesByType) =>
		Object.values(appearancesByType),
	);
	const requestCount = appearances.reduce((total, stats) => total + stats.total, 0);
	const preloadedLinkCount = Object.keys(window['__SSR_SMARTLINKS__'] ?? {}).length;

	// TODO: Replace this temporary Smart Link preload count with the bounded outbound-query count.
	return Math.min(requestCount || preloadedLinkCount, MAX_BYLINE_CONNECTION_ICONS);
};

const placeholderIcons: BylineConnectionIcon[] = Array.from(
	{ length: MAX_BYLINE_CONNECTION_ICONS },
	(_, index) => ({
		key: `placeholder-link-${index}`,
		label: '',
		icon: {
			kind: 'element',
			key: `placeholder-link-${index}`,
			label: '',
			node: (
				<LinkIcon
					label=""
					color={token('color.icon.disabled')}
					testId="byline-connections-ssr-placeholder-icon"
				/>
			),
		},
	}),
);

type BylineConnectionsPlaceholderProps = {
	fallback?: ReactNode;
};

export const BylineConnectionsPlaceholder = ({
	fallback = null,
}: BylineConnectionsPlaceholderProps): React.JSX.Element => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { formatMessage } = useIntl();
	const outboundLinkCount = getPreloadedOutboundLinkCount();

	if (!renderServerPlaceholder || outboundLinkCount === 0) {
		return <>{fallback}</>;
	}

	return (
		<span
			css={styles.wrapper}
			role="status"
			aria-label={formatMessage(i18n.loadingConnections)}
			data-testid="byline-connections-ssr-placeholder"
		>
			<span css={styles.trigger} aria-hidden="true">
				<BylineConnectionIconStack
					links={[]}
					additionalIcons={placeholderIcons.slice(0, outboundLinkCount)}
					appearance="placeholder"
				/>
				{formatMessage(i18n.teamworkGraph)}
			</span>
		</span>
	);
};
